
import React, { useState, useMemo, useEffect } from 'react';
import { UserProfile, Expert } from '../../../types';
import { Loader2, Sparkles, Send, ArrowLeft, Trash2, X, AlertTriangle, CheckSquare } from 'lucide-react';

interface Comment {
    id: number;
    target_type: string;
    target_id: number;
    user_id: number;
    comment: string;
    star: number | null;
    reply_id: number | null;
    created_at: string;
    user_name: string;
    user_avatar: string | null;
    target_name: string;
    is_expert: number;
}

interface GeneratedComment {
    id: number; // temp ID from AI
    user_id: number;
    content: string;
    rating: number | null;
    date: string;
    reply_id: number | null;
    // Frontend only state
    status: 'pending' | 'publishing' | 'success' | 'error';
    error?: string;
    db_id?: number; // Real ID after publish
    replies?: GeneratedComment[]; // For tree view
}


interface Props {
    isOpen: boolean;
    onClose: () => void;
    currentUser: UserProfile;
    bots: UserProfile[];
    articles: { id: number; title: string }[];
    experts: Expert[];
    allComments: Comment[];
    onPublish: () => Promise<void>;
}

const CommentGeneratorModal: React.FC<Props> = ({ isOpen, onClose, currentUser, bots, articles, experts, allComments, onPublish }) => {
    const [step, setStep] = useState<'form' | 'generating' | 'review'>('form');
    const [error, setError] = useState<string | null>(null);
    const [generatedComments, setGeneratedComments] = useState<GeneratedComment[]>([]);
    const [isPublishing, setIsPublishing] = useState(false);
    const [faultyJson, setFaultyJson] = useState<string | null>(null);
    const [rawJsonResponse, setRawJsonResponse] = useState<string | null>(null);
    
    // Form state
    const [formState, setFormState] = useState({
        generationMode: 'main' as 'main' | 'reply',
        target_type: 'expert',
        target_id: '',
        numComments: 5,
        dateFrom: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        dateTo: new Date().toISOString().slice(0, 10),
        ratingFrom: 4,
        ratingTo: 5,
        numRatings: 3,
        lengthFrom: 1,
        lengthTo: 1000
    });
    
    const [prompt, setPrompt] = useState('');

    const targetList = useMemo(() => {
        if (formState.target_type === 'article') return articles.map(a => ({ id: a.id, name: a.title }));
        if (formState.target_type === 'expert') return experts.map(e => ({ id: e.rowid, name: e.name }));
        return [];
    }, [formState.target_type, articles, experts]);
    
    const formattedJsonResponse = useMemo(() => {
        if (!rawJsonResponse) return null;
        try {
            const parsed = JSON.parse(rawJsonResponse);
            return JSON.stringify(parsed, null, 2);
        } catch (e) {
            return rawJsonResponse; // Fallback for invalid JSON
        }
    }, [rawJsonResponse]);


    // Update prompt when target changes
    useEffect(() => {
        const target = targetList.find(t => String(t.id) === formState.target_id);
        const expertDetails = formState.target_type === 'expert' ? experts.find(e => String(e.rowid) === formState.target_id) : null;

        if (target) {
            let existingCommentsForTarget = allComments
                .filter(c => c.target_type === formState.target_type && String(c.target_id) === formState.target_id);

            // In reply mode, filter existing comments to only include those created before the end of the reply date range.
            // This makes the prompt smaller and more relevant.
            if (formState.generationMode === 'reply') {
                const endDate = new Date(formState.dateTo);
                endDate.setHours(23, 59, 59, 999); // Set to end of the day

                existingCommentsForTarget = existingCommentsForTarget.filter(c => {
                    const commentDate = new Date(c.created_at);
                    return commentDate <= endDate;
                });
            }
            
            const existingCommentsText = existingCommentsForTarget.map(c => `ID ${c.id} by ${c.user_name} (on ${c.created_at}): "${c.comment}"`).join('\n');
            const hasExistingComments = existingCommentsForTarget.length > 0;

            if (formState.generationMode === 'reply' && !hasExistingComments) {
                setFormState(p => ({ ...p, generationMode: 'main' }));
                return;
            }
            
            const botsList = bots.map(b => `ID ${b.id}: ${b.name}`).join('\n');

            let targetDetails = `Name: ${target.name}`;
            if (expertDetails) {
                targetDetails += `\nDescription: ${expertDetails.description}\nBio: ${expertDetails.bio}`;
            }
            
            let newPrompt = '';

            const commonInstructions = `
2.  Assign a \`user_id\` to each comment from the "Available Users" list.
3.  **Tracking ID**: You MUST assign a unique numeric \`id\` to each comment. This is a critical identifier for the UI and cannot be omitted.
4.  Each comment’s \`content\` length must fall between ${formState.lengthFrom} and ${formState.lengthTo} characters, with a deliberately uneven distribution: around 10% of comments should be close to the lower bound, around 10% close to the upper bound, and the remaining ~80% randomly spread across the full range to create a wide length variance. Very short comments are allowed and should feel natural — they may consist of a single character, visual emoji (e.g. ❤️😀😘), emoticon (e.g. :), ;), :D) or brief casual words like ok, fajne, super, moc, etc.
5.  The \`date\` for each comment must be a valid ISO 8601 string within the range of ${formState.dateFrom} to ${formState.dateTo}.
6.  The \`content\` should be realistic and human-like: use varied tones and pacing, occasionally reference the target’s content, introduce natural imperfections — include spelling mistakes in ~15% of generated words, stylistic awkwardness or clumsy phrasing in ~30% of words, freely mix punctuation (missing commas, extra dots, double exclamation marks, etc.), write entire comments in ALL CAPS or all lowercase in ~10% of cases, and omit Polish diacritics in ~10% of comments. 10% of comments should have Emojis (e.g. ❤️😀😘) and should appear sparingly and inconsistently, as real users would use them. Comments should not mention in-person visits or physical meetings with the expert; assume the work is done remotely, so feedback should refer to online advice, consultation, guidance, or received insight rather than a visit.
7.  The final output must be a valid JSON array of objects.`;

            const jsonFormatDefinition = `
**JSON FORMAT (CRITICAL):**
Your response MUST be a valid JSON array. Each object in the array must follow this exact structure:
[
  {
    "id": number,       // Temporary unique ID, starting from 0
    "user_id": number,    // ID from the 'Available Users' list
    "content": string,    // The comment text
    "rating": number | null, // A number between X and Y, or null
    "date": string,       // ISO 8601 format date string
    "reply_id": number | null // ID of the comment being replied to, or null
  }
]
`;

            if (formState.generationMode === 'main') {
                 newPrompt = `
You are an advanced AI simulating human users. Your task is to generate a list of realistic, new, top-level comments.

**CONTEXT:**
- Target Type: ${formState.target_type}
- Target Details:
  """
  ${targetDetails}
  """
- Available Users for Commenting (Bots):
  """
  ${botsList}
  """

${jsonFormatDefinition}

**INSTRUCTIONS (Generate Main Comments):**
1.  Generate exactly ${formState.numComments} new, unique, top-level comments.
${commonInstructions}
8.  **User Continuity**: If you assign the same \`user_id\` to multiple comments for this target, their content must be coherent. They should read like they come from the same person over time (e.g., a follow-up, an update, or a second experience).
9.  **Replies**: ALL comments must be new top-level threads. The \`reply_id\` field MUST be \`null\`.
10. **Ratings (CRITICAL RULES)**:
    - From the ${formState.numComments} total comments you generate, EXACTLY **${formState.numRatings}** of them MUST have a \`rating\` value.
    - The other **${formState.numComments - formState.numRatings}** comments MUST have their \`rating\` value set to \`null\`.
    - For the comments that receive a rating, the value MUST be an integer between **${formState.ratingFrom}** and **${formState.ratingTo}**.
    - If a comment includes a \`rating\`, its content must clearly state that the commenter has used the expert's advice.
    - Preferentially assign ratings to comments that sound like they are from mothers.
                `.trim();
            } else { // 'reply' mode
                 const replyCommonInstructions = `
2.  Assign a \`user_id\` to each comment from the "Available Users" list.
3.  **Tracking ID**: You MUST assign a unique numeric \`id\` to each comment. This is a critical identifier for the UI and cannot be omitted.
4.  Each comment’s \`content\` length must fall between ${formState.lengthFrom} and ${formState.lengthTo} characters, with a deliberately uneven distribution: around 10% of comments should be close to the lower bound, around 10% close to the upper bound, and the remaining ~80% randomly spread across the full range to create a wide length variance. Very short comments are allowed and should feel natural — they may consist of a single character, visual emoji (e.g. ❤️😀😘), emoticon (e.g. :), ;), :D) or brief casual words like ok, fajne, super, moc, etc.
5.  The \`date\` for each comment must be a valid ISO 8601 string within the range of ${formState.dateFrom} to ${formState.dateTo}. **Crucially, a reply's date MUST be after the date of the comment it is replying to.**
6.  The \`content\` should be realistic and human-like: use varied tones and pacing, occasionally reference the target’s content, introduce natural imperfections — include spelling mistakes in ~15% of generated words, stylistic awkwardness or clumsy phrasing in ~30% of words, freely mix punctuation (missing commas, extra dots, double exclamation marks, etc.), write entire comments in ALL CAPS or all lowercase in ~10% of cases, and omit Polish diacritics in ~10% of comments. 10% of comments should have Emojis (e.g. ❤️😀😘) and should appear sparingly and inconsistently, as real users would use them. Comments should not mention in-person visits or physical meetings with the expert; assume the work is done remotely, so feedback should refer to online advice, consultation, guidance, or received insight rather than a visit.
7.  The final output must be a valid JSON array of objects.`;

                 newPrompt = `
You are an advanced AI simulating human users. Your task is to generate realistic replies to existing comments.

**CONTEXT:**
- Target Type: ${formState.target_type}
- Target Details:
  """
  ${targetDetails}
  """
- Existing Comments (You MUST reply to these, note their dates):
  """
  ${existingCommentsText}
  """
- Available Users for Commenting (Bots):
  """
  ${botsList}
  """

${jsonFormatDefinition}

**INSTRUCTIONS (Generate Replies):**
1.  Generate exactly ${formState.numComments} new, unique replies.
${replyCommonInstructions}
8.  **User Continuity**: If you assign the same \`user_id\` to multiple replies for this target, ensure their content is coherent. The replies should reflect a continuing conversation from that user's perspective.
9.  **Replies**: ALL comments MUST be replies to comments from the "Existing Comments" list. Assign a valid \`reply_id\` from that list to each generated comment. Do NOT generate any top-level comments (\`reply_id\` cannot be \`null\`).
10. **Ratings**: Do NOT assign any ratings. The \`rating\` field for all generated comments MUST be \`null\`.
                `.trim();
            }
            setPrompt(newPrompt);
        } else {
            setPrompt('Select a target to generate the prompt.');
        }
    }, [formState, allComments, bots, targetList, experts]);

    const sanitizeAndSetGeneratedComments = (data: GeneratedComment[]) => {
        const sanitizedData = data.map((c: any) => {
            const sanitizedComment = { ...c, status: 'pending' };
            if (formState.generationMode === 'main') {
                sanitizedComment.reply_id = null;
            } else if (formState.generationMode === 'reply') {
                sanitizedComment.rating = null; // Force null rating for replies
            }
            return sanitizedComment;
        });
        setGeneratedComments(sanitizedData);
        setStep('review');
    };

    const handleGenerate = async () => {
        setStep('generating');
        setError(null);
        setFaultyJson(null);
        setRawJsonResponse(null);

        try {
            const res = await fetch('/api/cms/generate-comments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prompt })
            });

            const text = await res.text();
            setRawJsonResponse(text);

            if (!res.ok) {
                try {
                    const err = JSON.parse(text);
                    throw new Error(err.error || 'API Error');
                } catch {
                    throw new Error(text || 'API Error');
                }
            }
            
            try {
                const data = JSON.parse(text);
                sanitizeAndSetGeneratedComments(data);
            } catch {
                setError("The string did not match the expected pattern. AI returned invalid JSON.");
                setFaultyJson(text);
                setStep('form');
            }

        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : 'An unknown error occurred.';
            setError(message);
            setStep('form');
        }
    };

    const handleFixJson = async () => {
        if (!faultyJson) return;

        setStep('generating');
        setError(null);

        const fixPrompt = `
The following text is supposed to be a valid JSON array of objects with this structure:
[{ "id": int, "user_id": int, "content": string, "rating": int | null, "date": string, "reply_id": int | null }]
However, it has formatting errors. Please fix it and return ONLY the corrected, valid JSON array. Do not add any explanatory text, introductory phrases, or markdown formatting like \`\`\`json. Just output the raw, corrected JSON string.

BROKEN JSON:
${faultyJson}
        `.trim();

        try {
            const res = await fetch('/api/cms/generate-comments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prompt: fixPrompt })
            });
            
            const text = await res.text();
            setRawJsonResponse(text);
            setFaultyJson(null);

            if (!res.ok) {
                 try {
                    const err = JSON.parse(text);
                    throw new Error(err.error || 'API Fix Error');
                } catch {
                    throw new Error(text || 'API Fix Error');
                }
            }

            try {
                const data = JSON.parse(text);
                sanitizeAndSetGeneratedComments(data);
            } catch {
                setError("Automatic fix failed. The format is still incorrect. You can try to fix it manually in the prompt.");
                setFaultyJson(text);
                setStep('form');
            }

        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : 'An unknown error occurred.';
            setError(message);
            setStep('form');
        }
    };

    const publishBatch = async (commentsToPublish: GeneratedComment[]) => {
        if (commentsToPublish.length === 0) return;
        setIsPublishing(true);
    
        const publishingIds = new Set(commentsToPublish.map(c => c.id));
        setGeneratedComments(prev => prev.map(c => 
            publishingIds.has(c.id) ? { ...c, status: 'publishing', error: undefined } : c
        ));
    
        try {
            const payload = commentsToPublish.map(c => {
                let finalReplyId = c.reply_id;
                // Don't resolve parent ID for replies to existing DB comments
                if (finalReplyId !== null && !allComments.some(ac => ac.id === finalReplyId)) {
                    const parentInList = generatedComments.find(p => p.id === finalReplyId);
                    if (parentInList?.status === 'success' && parentInList.db_id) {
                        finalReplyId = parentInList.db_id;
                    }
                }
    
                return {
                    id: c.id,
                    target_type: formState.target_type,
                    target_id: parseInt(formState.target_id),
                    user_id: c.user_id,
                    comment: c.content,
                    reply_id: finalReplyId,
                    star: c.rating,
                    created_at: c.date
                };
            });
    
            const res = await fetch('/api/cms/comments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify(payload)
            });
            
            const text = await res.text();
            if (!res.ok) {
                try {
                    const errData = JSON.parse(text);
                    throw new Error(errData.error || `Server responded with status ${res.status}`);
                } catch {
                    throw new Error(`Server error: ${res.status} - ${text.substring(0, 200)}...`);
                }
            }
            const data = JSON.parse(text);

            setGeneratedComments(prev => prev.map(c => {
                const result = data.results?.[c.id];
                if (result) {
                    return { ...c, status: result.status, db_id: result.db_id, error: result.error };
                }
                return c;
            }));
    
        } catch (e: unknown) {
             let errorMessage = e instanceof Error ? e.message : 'An unknown error occurred.';
            if (e instanceof TypeError && e.message.includes('Failed to fetch')) {
                errorMessage = "Network error: Failed to connect to the server.";
            }
            setGeneratedComments(prev => prev.map(c => 
                publishingIds.has(c.id) ? { ...c, status: 'error', error: errorMessage } : c
            ));
        } finally {
            setIsPublishing(false);
            await onPublish();
        }
    };
    
    const handlePublish = async () => {
        const commentsToProcess = generatedComments.filter(c => c.status === 'pending' || c.status === 'error');
        await publishBatch(commentsToProcess);
    };

    const handleRetry = async (commentId: number) => {
        const commentToRetry = generatedComments.find(c => c.id === commentId);
        if (commentToRetry) {
            await publishBatch([commentToRetry]);
        }
    };
    
    const updateGeneratedComment = (id: number, field: keyof GeneratedComment, value: string | number | null) => {
        setGeneratedComments(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
    };

    const removeGeneratedComment = (id: number) => {
        setGeneratedComments(prev => prev.filter(c => c.id !== id));
    };

    const commentTree = useMemo(() => {
        const commentMap = new Map<number, GeneratedComment>();
        generatedComments.forEach(c => commentMap.set(c.id, { ...c, replies: [] }));

        const roots: GeneratedComment[] = [];

        generatedComments.forEach(c => {
            if (c.reply_id !== null && commentMap.has(c.reply_id)) {
                 const parent = commentMap.get(c.reply_id);
                 parent?.replies?.push(c);
            } else {
                roots.push(c);
            }
        });
        return roots;
    }, [generatedComments]);
    
    if (!isOpen) return null;
    
    const commentsToPublishCount = generatedComments.filter(c => c.status === 'pending' || c.status === 'error').length;
    
    const GeneratedCommentNode: React.FC<{ comment: GeneratedComment, level: number }> = ({ comment, level }) => (
        <div className={`relative ${level > 0 ? 'ml-6' : ''}`}>
            {level > 0 && <div className="absolute -left-3 top-4 w-3 h-px bg-gray-600"></div>}
            {level > 0 && <div className="absolute -left-3 top-4 h-full w-px bg-gray-600"></div>}

            <div className={`p-3 rounded-lg flex gap-3 transition-colors border ${
                comment.status === 'success' ? 'bg-green-900/20 border-green-800' :
                comment.status === 'error' ? 'bg-red-900/20 border-red-800' :
                'bg-mystic-inset border-gray-700'
            }`}>
                <div className="w-8 flex justify-center pt-1">
                    {comment.status === 'publishing' && <Loader2 size={16} className="animate-spin text-blue-400"/>}
                    {comment.status === 'success' && <CheckSquare size={16} className="text-green-500"/>}
                    {comment.status === 'error' && <AlertTriangle size={16} className="text-red-500"/>}
                </div>
                <div className="space-y-2 flex-1">
                    <div className="flex gap-2 flex-wrap">
                        <select value={comment.user_id} onChange={e => updateGeneratedComment(comment.id, 'user_id', Number(e.target.value))} className="bg-mystic-bg border border-gray-600 rounded p-1 text-xs h-8" disabled={isPublishing || comment.status === 'success'}>
                            {bots.map(bot => <option key={bot.id} value={bot.id}>{bot.name}</option>)}
                        </select>
                        <input type="datetime-local" value={comment.date.slice(0, 16)} onChange={e => updateGeneratedComment(comment.id, 'date', new Date(e.target.value).toISOString())} className="bg-mystic-bg border border-gray-600 rounded p-1 text-xs h-8 w-40 text-gray-300" disabled={isPublishing || comment.status === 'success'}/>
                        <select value={comment.rating ?? ''} onChange={e => updateGeneratedComment(comment.id, 'rating', e.target.value ? Number(e.target.value) : null)} className="bg-mystic-bg border border-gray-600 rounded p-1 text-xs h-8" disabled={isPublishing || comment.status === 'success'}>
                            <option value="">No rating</option>
                            {[1, 2, 3, 4, 5].map(s => <option key={s} value={s}>{s} Star(s)</option>)}
                        </select>
                    </div>
                    <textarea value={comment.content} onChange={e => updateGeneratedComment(comment.id, 'content', e.target.value)} className="w-full flex-1 bg-mystic-bg border border-gray-600 rounded p-2 text-white text-sm h-auto resize-y" disabled={isPublishing || comment.status === 'success'}/>
                    {comment.status === 'error' && (
                        <div className="p-2 bg-red-900/30 rounded text-red-300 text-xs flex justify-between items-center">
                            <span>Error: {comment.error}</span>
                            <button onClick={() => handleRetry(comment.id)} className="font-bold hover:underline">Retry</button>
                        </div>
                    )}
                </div>
                <button onClick={() => removeGeneratedComment(comment.id)} disabled={isPublishing} className="text-gray-600 hover:text-red-400 p-1 self-start disabled:opacity-50"><Trash2 size={16}/></button>
            </div>
            
            {comment.replies && comment.replies.length > 0 && (
                <div className="mt-3 space-y-3">
                    {comment.replies.map(reply => <GeneratedCommentNode key={reply.id} comment={reply} level={level + 1} />)}
                </div>
            )}
        </div>
    );
    
    const hasExistingCommentsForTarget = useMemo(() => {
        if (!formState.target_id) return false;
        return allComments.some(c => c.target_type === formState.target_type && String(c.target_id) === formState.target_id);
    }, [allComments, formState.target_type, formState.target_id]);

    return (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-mystic-bg border border-gray-700 w-full max-w-4xl h-[90vh] rounded-xl shadow-2xl flex flex-col relative">
                <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-mystic-box rounded-t-xl">
                    <div className="flex items-center gap-3">
                        {step !== 'form' && <button onClick={() => setStep('form')}><ArrowLeft className="w-5 h-5 text-gray-400 hover:text-white"/></button>}
                        <h3 className="text-lg font-bold text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-mystic-accent"/> Comment Generator</h3>
                    </div>
                    <button onClick={onClose}><X className="w-5 h-5 text-gray-500 hover:text-white"/></button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6">
                    {step === 'form' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                               <div className="flex items-center gap-2 bg-mystic-inset border border-gray-700 p-1 rounded-lg">
                                    <button 
                                        type="button" 
                                        onClick={() => setFormState(p => ({...p, generationMode: 'main'}))}
                                        className={`flex-1 text-center text-sm font-bold py-1.5 rounded-md transition-colors ${formState.generationMode === 'main' ? 'bg-mystic-accent text-black' : 'text-gray-400 hover:bg-gray-800'}`}
                                    >
                                        Generate Main Comments
                                    </button>
                                    <button 
                                        type="button" 
                                        onClick={() => setFormState(p => ({...p, generationMode: 'reply'}))}
                                        disabled={!hasExistingCommentsForTarget}
                                        className={`flex-1 text-center text-sm font-bold py-1.5 rounded-md transition-colors ${formState.generationMode === 'reply' ? 'bg-mystic-accent text-black' : 'text-gray-400 hover:bg-gray-800'} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    >
                                        Generate Replies
                                    </button>
                               </div>

                               <select name="target_type" value={formState.target_type} onChange={(e) => setFormState(p => ({...p, target_type: e.target.value, target_id: ''}))} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white">
                                    <option value="expert">Expert</option>
                                    <option value="article">Article</option>
                                    <option value="product" disabled>Product (soon)</option>
                                    <option value="horoscope" disabled>Horoscope (soon)</option>
                                </select>
                                <select name="target_id" value={formState.target_id} onChange={(e) => setFormState(p => ({...p, target_id: e.target.value}))} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white" required>
                                     <option value="">-- Select Target --</option>
                                     {targetList.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                                </select>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-400">{formState.generationMode === 'main' ? '# Comments' : '# Replies'}</label>
                                        <input 
                                            type="number" 
                                            name="numComments" 
                                            value={formState.numComments} 
                                            onChange={(e) => {
                                                const newNum = Number(e.target.value);
                                                setFormState(p => ({
                                                    ...p,
                                                    numComments: newNum,
                                                    numRatings: Math.min(p.numRatings, newNum)
                                                }));
                                            }} 
                                            className="w-full bg-mystic-inset border border-gray-700 p-2 rounded mt-1"
                                        />
                                    </div>
                                    
                                    {formState.generationMode === 'main' && (
                                        <>
                                            <div>
                                                <label className="text-xs text-gray-400"># with Ratings</label>
                                                <input 
                                                    type="number" 
                                                    name="numRatings" 
                                                    value={formState.numRatings} 
                                                    max={formState.numComments}
                                                    onChange={(e) => {
                                                        const newNum = Number(e.target.value);
                                                        setFormState(p => ({
                                                            ...p,
                                                            numRatings: Math.max(0, Math.min(newNum, p.numComments))
                                                        }));
                                                    }} 
                                                    className="w-full bg-mystic-inset border border-gray-700 p-2 rounded mt-1"
                                                />
                                            </div>
                                            <div><label className="text-xs text-gray-400">Rating From</label><input type="number" min="1" max="5" name="ratingFrom" value={formState.ratingFrom} onChange={(e) => setFormState(p => ({...p, ratingFrom: Number(e.target.value)}))} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded mt-1"/></div>
                                            <div><label className="text-xs text-gray-400">Rating To</label><input type="number" min="1" max="5" name="ratingTo" value={formState.ratingTo} onChange={(e) => setFormState(p => ({...p, ratingTo: Number(e.target.value)}))} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded mt-1"/></div>
                                        </>
                                    )}
                                    
                                    <div><label className="text-xs text-gray-400">Length From</label><input type="number" name="lengthFrom" value={formState.lengthFrom} onChange={(e) => setFormState(p => ({...p, lengthFrom: Number(e.target.value)}))} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded mt-1"/></div>
                                    <div><label className="text-xs text-gray-400">Length To</label><input type="number" name="lengthTo" value={formState.lengthTo} onChange={(e) => setFormState(p => ({...p, lengthTo: Number(e.target.value)}))} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded mt-1"/></div>
                                    <div><label className="text-xs text-gray-400">Date From</label><input type="date" name="dateFrom" value={formState.dateFrom} onChange={(e) => setFormState(p => ({...p, dateFrom: e.target.value}))} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded mt-1 text-gray-300"/></div>
                                    <div><label className="text-xs text-gray-400">Date To</label><input type="date" name="dateTo" value={formState.dateTo} onChange={(e) => setFormState(p => ({...p, dateTo: e.target.value}))} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded mt-1 text-gray-300"/></div>
                                </div>
                                {error && <p className="text-red-500 text-xs">{error}</p>}
                                {faultyJson && (
                                    <div className="mt-4 p-4 bg-red-900/20 border border-red-800 rounded-lg space-y-2">
                                        <h4 className="text-red-300 font-bold text-sm">Faulty AI Response:</h4>
                                        <textarea
                                            readOnly
                                            value={faultyJson}
                                            className="w-full h-24 bg-black/50 border border-red-700/50 rounded p-2 text-red-200 text-xs font-mono"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleFixJson}
                                            className="bg-yellow-600 hover:bg-yellow-500 text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2"
                                        >
                                            <Sparkles size={16} />
                                            Try to Fix it with AI
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col">
                                <label className="text-xs text-gray-400 mb-2">Generated Prompt (Editable)</label>
                                <textarea 
                                    value={prompt} 
                                    onChange={(e) => setPrompt(e.target.value)}
                                    className="w-full flex-1 bg-black/30 border border-gray-700 rounded-lg p-3 text-gray-300 text-xs resize-none focus:border-mystic-accent focus:ring-0 outline-none"
                                />
                            </div>
                        </div>
                    )}
                    {step === 'generating' && (
                        <div className="flex flex-col items-center justify-center h-full gap-4 text-gray-400">
                            <Loader2 className="w-12 h-12 animate-spin text-mystic-accent"/>
                            <p>Generating comments... this may take a moment.</p>
                        </div>
                    )}
                     {step === 'review' && (
                        <div className="space-y-4">
                           {rawJsonResponse && (
                               <details className="bg-mystic-inset border border-gray-700 rounded-lg">
                                   <summary className="p-2 text-xs font-bold text-gray-400 cursor-pointer">Zobacz surową odpowiedź AI</summary>
                                   <pre className="p-3 bg-black/30 text-xs text-gray-300 overflow-auto max-h-48 rounded-b-lg">{formattedJsonResponse}</pre>
                               </details>
                           )}
                           <div className="space-y-3">
                                {commentTree.map(comment => <GeneratedCommentNode key={comment.id} comment={comment} level={0} />)}
                           </div>
                        </div>
                    )}
                </div>
                
                <div className="p-4 border-t border-gray-800 flex justify-end">
                    {step === 'form' && <button onClick={handleGenerate} disabled={!formState.target_id} className="bg-mystic-accent text-black font-bold px-6 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50">Generate</button>}
                    {step === 'review' && (
                        <button onClick={handlePublish} disabled={isPublishing || commentsToPublishCount === 0} className="bg-green-600 text-white font-bold px-6 py-2 rounded-lg disabled:opacity-50 transition-colors">
                            {isPublishing ? 'Publishing...' : `Publish ${commentsToPublishCount > 0 ? `(${commentsToPublishCount})` : ''}`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CommentGeneratorModal;
